// @ts-ignore
import { useSyncExternalStore } from 'react';

/**
 * Global store similar to React Context. (An atom is any value such as objects, arrays, strings, numbers, booleans, etc.)
 *
 *  - Declaring atoms:
 *
 *  #### Standard
 *  export const salaryAtom = atom(100000);
 *  export const bonusAtom = atom(10000);
 *
 *  #### Computed
 *  export const totalSalaryAtom = atom((get) => get(salaryAtom) + get(bonusAtom));
 *
 *  #### Asynchronous
 *  export const dataAtom = atom(() =>
 *  fetch("/data.json").then((response) => response.json())
 *  );
 *
 *  #### Dependant on an async
 *  export const keysAtom = atom((get) => Object.keys(get(dataAtom) ?? {}));
 *
 * - To use an atom value:
 *
 * const [salary, setSalary] = useAtom(salaryAtom);
 *
 * -- or just the value:
 * const totalSalary = useAtomValue(totalSalaryAtom);
 */

export interface Atom<AtomType> {
  get: () => AtomType;
  set: (newValue: AtomType) => void;
  subscribe: (callback: (newValue: AtomType) => void) => () => void;
}

type AtomGetter<AtomType> = (get: <T>(a: Atom<T>) => T) => AtomType;

export function atom<AtomType>(initialValue: AtomType | AtomGetter<AtomType>): Atom<AtomType> {
  let value = typeof initialValue === 'function' ? (null as unknown as AtomType) : initialValue;

  const subscribers = new Set<(newValue: AtomType) => void>();

  function get<T>(atom: Atom<T>) {
    let currentValue = atom.get();
    atom.subscribe(newValue => {
      if (newValue === currentValue) return;

      currentValue = newValue;
      computeValue();
      subscribers.forEach(callback => callback(value));
    });

    return currentValue;
  }

  function computeValue() {
    const newValue = typeof initialValue === 'function' ? (initialValue as AtomGetter<AtomType>)(get) : value;

    if (newValue && typeof (newValue as any).then === 'function') {
      value = null as unknown as AtomType;
      (newValue as unknown as Promise<AtomType>).then(resolvedValue => {
        value = resolvedValue;
        subscribers.forEach(callback => callback(value));
      });
    } else {
      value = newValue;
    }
  }

  computeValue();

  return {
    get: () => value,
    set: newValue => {
      value = newValue;
      // Notify each subscriber of the updated value
      subscribers.forEach(callback => callback(value));
    },
    subscribe: callback => {
      subscribers.add(callback);

      return () => {
        subscribers.delete(callback);
      };
    }
  };
}

export function useAtom<AtomType>(atom: Atom<AtomType>): [AtomType, (newValue: AtomType) => void] {
  return [useSyncExternalStore(atom.subscribe, atom.get), atom.set];
}

export function useAtomValue<AtomValue>(atom: Atom<AtomValue>) {
  return useSyncExternalStore(atom.subscribe, atom.get);
}
